import type {
  BindingSource,
  ConsolidatedBindingContext,
} from '@packages/systems/dynamo/gateway';
import type {EmbedTokenOption} from '@packages/systems/core/utils/EmbedUtils/types';

export const getBindingContextTokenOptions = (
  bindingContext: ConsolidatedBindingContext,
  isPageSettings: boolean = false
): EmbedTokenOption[] => {
  const bindingSources = bindingContext.getExpandedBindingSources();
  return bindingSources.reduce((tokenOptions, bindingSource) => {
    // Custom code tokens, e.g. `{{wf {"path":"author:name","type":"PlainText"} }}`
    // can only handle paths of length 1 and 2. We ignore the binding sources that
    // have longer paths because we don't have mechanisms to render such tokens.
    if (
      bindingSource.path.length > 2 ||
      bindingSource.rendering.strategy === 'client'
    ) {
      return tokenOptions;
    }

    // We currently don't support full slug binding within the
    // page settings bindings
    if (isPageSettings && bindingSource.type === 'FullSlug') {
      return tokenOptions;
    }

    switch (bindingSource.type) {
      case 'PlainText':
      case 'Slug':
      case 'Number':
      case 'Link':
      case 'Date':
      case 'HighlightedText':
      case 'Option':
      case 'Bool':
      case 'Color':
      case 'Email':
      case 'Phone':
      case 'ImageRef':
      case 'FileRef':
      case 'FullSlug':
      case 'CommercePrice': {
        // This expansion comes from an item ref field, which we don't
        // support FullSlug field requests for item refs.
        if (bindingSource.type === 'FullSlug' && bindingSource.parentId) {
          return tokenOptions;
        }
        tokenOptions.push(createTokenOption(bindingSource, bindingSources));
      }
    }
    return tokenOptions;
  }, Array<EmbedTokenOption>());
};

function createTokenOption(
  bindingSource: BindingSource,
  bindingSources: BindingSource[]
): EmbedTokenOption {
  const label = createTokenLabel(bindingSource, bindingSources);
  const path = createTokenPath(bindingSource);
  const type = createTokenType(bindingSource);
  const value = createTokenValue(path, type);

  return {label, value, type};
}

function createTokenLabel(
  bindingSource: BindingSource,
  bindingSources: BindingSource[]
): string {
  const {label, parentId} = bindingSource;

  const parentSourceLabel = parentId
    ? findParentBindingSourceLabel(parentId, bindingSources)
    : null;

  return parentSourceLabel ? `${parentSourceLabel}: ${label}` : label;
}

function findParentBindingSourceLabel(
  parentId: string,
  bindingSources: Array<BindingSource>
): string | null {
  const parentBindingSource = bindingSources.find((bindingSource) => {
    return bindingSource.id === parentId;
  });
  return parentBindingSource?.label ?? null;
}

function createTokenPath(bindingSource: BindingSource): string {
  return bindingSource.path.map(({slug}) => slug).join(':');
}

function createTokenType(bindingSource: BindingSource): BindingSource['type'] {
  // Slug option uses the type PlainText for backward compatibility.
  return bindingSource.type === 'Slug' ? 'PlainText' : bindingSource.type;
}

function createTokenValue(path: string, type: string): string {
  if (type === 'Date') {
    return JSON.stringify({
      path,
      transformers: [{name: 'date', arguments: ['MMM DD, YYYY']}],
      type,
    });
  }
  return JSON.stringify({path, type});
}
